// Topic hubs: curated reading paths through existing guides, grouped by the job
// a reader is trying to do. Every slug must be a real blog post (checked in tests).

export interface TopicLink {
  /** Blog slug, resolved to its title at render time. */
  slug?: string;
  /** Non-blog destination such as a tool or service page. */
  href?: string;
  label?: string;
  note: string;
}

export interface TopicSection {
  heading: string;
  intro: string;
  links: TopicLink[];
}

export interface TopicFaq {
  question: string;
  answer: string;
}

export interface Topic {
  slug: 'snowflake' | 'sigma' | 'ecommerce-reporting';
  /** Blog tag that marks a post as part of this topic. */
  tag: string;
  name: string;
  title: string;
  seoTitle: string;
  description: string;
  intro: string;
  art: string;
  sections: TopicSection[];
  compareGroup?: 'bi' | 'ecomm';
  tools: { href: string; label: string; note: string }[];
  services: { href: string; label: string }[];
  faqs: TopicFaq[];
  updated: string;
}

export const topics: Topic[] = [
  {
    slug: 'snowflake',
    tag: 'snowflake',
    name: 'Snowflake',
    title: 'Snowflake guides',
    seoTitle: 'Snowflake guides: setup, connections, and cost',
    description: 'Practical Snowflake guides for account setup, connecting Excel, Claude, and ChatGPT, controlling credit spend, and choosing a warehouse for ecommerce data.',
    intro: 'Guides for teams running Snowflake without a full-time platform engineer. Start with the setup, connect the tools your team already uses, then keep an eye on what each warehouse costs.',
    art: '/images/editorial/snowflake.webp',
    sections: [
      {
        heading: 'Start here',
        intro: 'What Snowflake is and the first decisions to make in a new account.',
        links: [
          { slug: 'snowflake-explained', note: 'Storage, compute, and warehouses in plain language. Share it with stakeholders who keep asking.' },
          { slug: 'how-i-set-up-a-new-snowflake-account', note: 'The roles, warehouses, and grants to create before anyone loads data.' },
        ],
      },
      {
        heading: 'Connect the tools your team uses',
        intro: 'Step-by-step connections, including the settings that usually break.',
        links: [
          { slug: 'connect-excel-to-snowflake-with-private-key', note: 'Windows Excel over ODBC with key-pair authentication, plus the PRIV_KEY_FILE errors to expect.' },
          { slug: 'connect-excel-to-snowflake-with-private-key-mac', note: 'The macOS version: driver loading, User DSNs, and key-file access.' },
          { slug: 'connect-claude-to-snowflake', note: 'Snowflake MCP for Claude: account identifier, hostname, default role, and grants.' },
          { slug: 'connect-chatgpt-to-snowflake', note: 'The same setup for ChatGPT, with a least-privilege role and a read-only test query.' },
        ],
      },
      {
        heading: 'Define your metrics once',
        intro: 'Semantic layers in plain words, then built in Snowflake for dashboards and AI questions.',
        links: [
          { slug: 'what-is-a-semantic-layer', note: 'A data dictionary with the metrics defined: what goes in one, the tool options, and when you need it.' },
          { slug: 'snowflake-semantic-views-guide', note: 'CREATE SEMANTIC VIEW on an orders and customers model, how to query it, and how Cortex Analyst uses it.' },
        ],
      },
      {
        heading: 'Cost and performance',
        intro: 'Find out why credits moved before resizing anything.',
        links: [
          { slug: 'snowflake-warehouse-cost-spike-compilation-time', note: 'A credit spike that came from query compilation, not data volume, and how to spot it.' },
        ],
      },
      {
        heading: 'Choose and fill a warehouse',
        intro: 'Platform and pipeline decisions for brands bringing several sources together.',
        links: [
          { slug: 'modern-data-stack-tools-mapped', note: 'A map of 61 tools by the job they do, and which layers to set up first.' },
          { slug: 'bigquery-vs-snowflake-ecommerce', note: 'BigQuery, Snowflake, and Azure compared on pricing model and ecosystem fit.' },
          { slug: 'fivetran-vs-airbyte-vs-daton-ecommerce', note: 'Which loader to use for Shopify, Amazon, and ad platforms.' },
          { slug: 'open-source-klaviyo-snowflake-sync', note: 'An open-source sync that loads Klaviyo profiles and events into Snowflake in short, resumable runs.' },
          { slug: 'incremental-klaviyo-loads-watermarks-rate-limits', note: 'Watermarks, timestamp ties, lost cursors, and 429 retries in an incremental Klaviyo load, with code.' },
          { slug: 'bronze-silver-gold-ecommerce-order-example', note: 'How raw, cleaned, and reporting layers fit together, using one order.' },
        ],
      },
      {
        heading: 'Choose a partner',
        intro: 'Compare consulting firms before you sign a statement of work.',
        links: [
          { slug: 'best-snowflake-consulting-firms', note: 'Ten Snowflake consulting firms by fit, size, HQ, and partner tier, with sources.' },
        ],
      },
    ],
    tools: [
      { href: '/tools/reporting-time-calculator', label: 'Reporting time calculator', note: 'Estimate what manual reporting costs before you budget for automation.' },
      { href: '/tools/bi-tool-quiz', label: 'BI tool quiz', note: 'Which BI tool fits the warehouse you are setting up and the people who will build reports.' },
    ],
    services: [{ href: '/services/snowflake-consulting', label: 'Snowflake consulting' }],
    faqs: [
      {
        question: 'Do I need a separate warehouse for each team?',
        answer: 'Not at first. Separate warehouses are useful when you need to see who is spending credits or when one workload slows another down. Many small accounts start with one for loading and one for reporting.',
      },
      {
        question: 'Which authentication should Excel and BI tools use?',
        answer: 'Key-pair authentication keeps a Snowflake password out of each tool, and it works with the ODBC driver Excel uses. The Excel guides above cover the setup on Windows and Mac, including where the key file has to live.',
      },
      {
        question: 'Why did my Snowflake bill go up when data volume did not?',
        answer: 'Common causes are a warehouse that never suspends, a dashboard refreshing more often than anyone reads it, or queries that take a long time to compile. Check query history by warehouse before changing sizes.',
      },
    ],
    updated: '2026-11-04',
  },
  {
    slug: 'sigma',
    tag: 'sigma',
    name: 'Sigma',
    title: 'Sigma guides',
    seoTitle: 'Sigma guides: formulas, workbooks, and migrations',
    description: 'Sigma formula cheat sheets for Excel, Tableau, and DAX users, workbook how-tos, and a step-by-step path for planning, validating, and cutting over a migration to Sigma.',
    intro: 'Everything here comes from building Sigma workbooks and moving reports into Sigma from Tableau and Power BI. Translate the formulas first, then plan, validate, and cut over in that order.',
    art: '/images/editorial/bi-comparison.webp',
    sections: [
      {
        heading: 'Translate formulas you already know',
        intro: 'Worked examples with downloadable sample data.',
        links: [
          { slug: 'sigma-functions-list', note: 'All 239 Sigma functions by category, with syntax, a one-line explanation, and worked examples.' },
          { slug: 'excel-to-sigma-functions-cheat-sheet', note: 'Lookups, conditionals, dates, and text functions, with the Sigma version of each.' },
          { slug: 'tableau-to-sigma-functions-cheat-sheet', note: 'LOD expressions, table calculations, and parameters, and where they live in Sigma.' },
          { slug: 'power-bi-dax-to-sigma-formulas-cheat-sheet', note: 'CALCULATE, filter context, and time intelligence rewritten for Sigma.' },
        ],
      },
      {
        heading: 'Build in Sigma',
        intro: 'Specific workbook techniques.',
        links: [
          { slug: 'how-to-build-a-combo-chart-in-sigma', note: 'Bars and a line on one chart, with a second axis.' },
          { slug: 'how-to-merge-cells-in-a-sigma-table', note: 'Grouping instead of merged cells, and when a pivot table is the better choice.' },
          { slug: 'consumer-brand-retention-dashboard-in-sigma', note: 'A five-report retention workbook on synthetic data that you can open and explore.' },
          { slug: 'sigma-experts-to-follow', note: 'Ten Sigma builders and educators, and the public app or video to open first from each.' },
        ],
      },
      {
        heading: 'Plan a migration',
        intro: 'Scope, timeline, and cost before anyone rebuilds a dashboard.',
        links: [
          { slug: 'what-should-a-sigma-migration-assessment-include', note: 'What an assessment should produce, and what it should not promise.' },
          { slug: 'which-dashboards-to-migrate-consolidate-or-retire', note: 'Sort the inventory before it turns into the backlog.' },
          { slug: 'how-to-plan-a-tableau-to-sigma-migration', note: 'Tableau-specific planning: extracts, actions, and published sources.' },
          { slug: 'how-to-plan-a-power-bi-to-sigma-migration', note: 'Power BI-specific planning: semantic models, DAX, and row-level security.' },
          { slug: 'how-long-does-a-sigma-migration-take', note: 'What moves the timeline, phase by phase.' },
          { slug: 'power-bi-tableau-to-sigma-migration-cost', note: 'The drivers behind a quote, so you can compare proposals.' },
          { slug: '8-tests-for-choosing-a-sigma-migration-pilot', note: 'Pick a first report that actually tests the hard parts.' },
          { slug: '9-questions-to-ask-a-sigma-migration-consultant', note: 'Questions that separate a plan from a pitch.' },
        ],
      },
      {
        heading: 'Choose a partner',
        intro: 'Who does Sigma work, and who each firm fits.',
        links: [
          { slug: 'best-sigma-consulting-firms', note: 'Ten Sigma consulting firms by fit, size, HQ, and partner tier, with sources.' },
        ],
      },
      {
        heading: 'Validate and cut over',
        intro: 'Prove the new reports match before anyone switches.',
        links: [
          { slug: 'how-to-validate-a-sigma-migration', note: 'Compare source and target at the same data cutoff, case by case.' },
          { slug: '7-dax-measure-tests-before-migrating-to-sigma', note: 'Measure tests to write down before rebuilding Power BI logic.' },
          { slug: '7-tableau-interactions-to-review-before-moving-to-sigma', note: 'Filters, actions, and drill paths that users notice when they change.' },
          { slug: '10-checks-before-a-sigma-migration-cutover', note: 'The last checks before the old tool is switched off.' },
        ],
      },
    ],
    compareGroup: 'bi',
    tools: [
      { href: '/tools/bi-tool-quiz', label: 'BI tool quiz', note: 'Eight questions to see which of Sigma, Power BI, Tableau, Looker, Metabase, Hex, or Omni fits your team.' },
      { href: '/tools/tableau-to-sigma-migration-assessment', label: 'Tableau to Sigma migration assessment', note: 'Get a readout of size, rebuild risks, and a phase plan.' },
      { href: '/tools/power-bi-to-sigma-migration-assessment', label: 'Power BI to Sigma migration assessment', note: 'Get a readout of size, rebuild risks, and a phase plan.' },
      { href: '/tools/tableau-to-sigma-formula-translator', label: 'Tableau to Sigma formula translator', note: 'Paste a calculated field and get the Sigma version and what changes.' },
      { href: '/tools/dax-to-sigma-formula-translator', label: 'DAX to Sigma formula translator', note: 'Paste a DAX measure and get the Sigma version and what changes.' },
      { href: '/tools/sigma-migration-readiness', label: 'Sigma migration readiness check', note: 'Answer 16 questions to see which gaps to close before scoping.' },
      { href: '/resources/sigma-migration-checklist.md', label: 'Migration assessment checklist (Markdown)', note: 'The worksheet version to fill in with your team.' },
    ],
    services: [
      { href: '/services/sigma-consulting', label: 'Sigma consulting' },
      { href: '/services/tableau-to-sigma', label: 'Tableau to Sigma migration' },
      { href: '/services/power-bi-to-sigma', label: 'Power BI to Sigma migration' },
    ],
    faqs: [
      {
        question: 'Can Tableau or Power BI dashboards be converted to Sigma automatically?',
        answer: 'Not in a way you can trust without checking. The calculations, filters, and data models have to be rebuilt and then compared against the original, which is why validation gets its own section above.',
      },
      {
        question: 'Where should a migration start?',
        answer: 'With an inventory and one representative pilot report. The pilot shows how long the harder patterns take, which makes the rest of the estimate far more reliable.',
      },
      {
        question: 'Do I need Snowflake to use Sigma?',
        answer: 'No. Sigma runs on several cloud data warehouses, including Snowflake, BigQuery, Databricks, and Redshift. What matters more is that the data is modeled for reporting before it reaches Sigma.',
      },
    ],
    updated: '2026-11-03',
  },
  {
    slug: 'ecommerce-reporting',
    tag: 'ecommerce',
    name: 'Ecommerce reporting',
    title: 'Ecommerce reporting guides',
    seoTitle: 'Ecommerce reporting guides: Shopify, Amazon, and margin',
    description: 'Guides for ecommerce brands joining Shopify, Amazon, TikTok Shop, and marketing data: getting sources into a warehouse, making revenue and margin agree, and choosing metrics.',
    intro: 'Most ecommerce reporting problems are definition problems: which date, which channel, which fee. These guides follow the order the work usually happens, from loading sources to numbers people act on.',
    art: '/images/editorial/ecommerce-pipelines.webp',
    sections: [
      {
        heading: 'Get your sources in one place',
        intro: 'Which systems to connect and how to load them.',
        links: [
          { slug: 'ecommerce-data-stack-tools-mapped', note: 'A map of 57 tools from storefront to dashboard, with packaged and warehouse options side by side.' },
          { slug: 'ecommerce-data-stack-white-paper', note: 'Free white paper: which tools fit each stage, and a five-phase plan with owners and acceptance checks.' },
          { slug: 'common-ecommerce-data-sources-warehouse', note: 'The nine sources most brands end up needing, and what each one adds.' },
          { slug: 'how-to-combine-shopify-klaviyo-recharge-data', note: 'Join storefront, email, and subscription data into one customer view.' },
          { slug: 'how-to-track-customers-across-platforms', note: 'Identity and events tables for one customer timeline, with a working demo.' },
          { slug: 'where-do-amazon-seller-fees-live-in-the-sp-api', note: 'Which SP-API report actually holds each Amazon fee.' },
          { slug: 'fivetran-vs-airbyte-vs-daton-ecommerce', note: 'Choosing a loader for ecommerce and ad platforms.' },
          { slug: 'ecommerce-data-ingestion-tools', note: 'Fivetran, Airbyte, Daton, Funnel, Portable, Stitch, and custom code compared source by source, with lessons from running custom pipelines.' },
          { slug: 'open-source-klaviyo-snowflake-sync', note: 'An MIT-licensed Klaviyo to Snowflake sync for profiles and events: how it works, setup, and when a managed connector fits better.' },
          { slug: 'bigquery-vs-snowflake-ecommerce', note: 'Choosing the warehouse those sources land in.' },
        ],
      },
      {
        heading: 'Make the numbers agree',
        intro: 'The usual reasons two reports disagree, and how to fix each.',
        links: [
          { slug: 'why-shopify-finance-marketing-report-different-revenue', note: 'Three teams, three revenue numbers, and the definitions behind each.' },
          { slug: 'ecommerce-semantic-layer', note: 'A metric dictionary from gross sales to LTV, with grain, date, sources, owner, and a CSV template.' },
          { slug: 'shopify-refund-date-vs-order-date', note: 'Why monthly sales change depending on which date a refund uses.' },
          { slug: 'avoid-double-counting-shopify-amazon-tiktok-orders', note: 'Stop counting one order in two channels.' },
          { slug: 'map-skus-shopify-amazon-tiktok-margin', note: 'One product, three SKU codes, and the mapping table that fixes margin.' },
          { slug: 'why-amazon-profit-changes-after-sale', note: 'Estimated versus settled Amazon fees, and when profit stops moving.' },
        ],
      },
      {
        heading: 'Choose metrics that guide decisions',
        intro: 'Definitions to agree on before building the dashboard.',
        links: [
          { slug: 'daily-ecommerce-kpis-by-channel', note: 'What belongs in a morning scorecard, split by channel.' },
          { slug: 'blended-attributed-incremental-marginal-cac', note: 'Four kinds of CAC and which one should guide the next dollar.' },
          { slug: 'contribution-margin-ltv-by-acquisition-channel', note: 'Lifetime value on contribution margin, split by channel.' },
          { slug: 'consumer-brand-retention-dashboard-in-sigma', note: 'Repeat rate, frequency, product paths, and reactivation, each defined by who it includes.' },
          { slug: 'what-can-you-do-with-business-numbers-you-trust', note: 'The decisions that open up once reporting is reliable.' },
        ],
      },
      {
        heading: 'Choose a data partner',
        intro: 'Shortlists of firms that do this work, with how to pick between them.',
        links: [
          { slug: 'best-ecommerce-data-consultancies', note: 'Ten consultancies for DTC, Shopify, and marketplace brands: warehouses, attribution, margin, and retention.' },
          { slug: 'best-consumer-brand-analytics-consultancies', note: 'Ten partners for brands that also sell through retail and wholesale: POS, syndicated data, trade, and planning.' },
        ],
      },
      {
        heading: 'Model and report',
        intro: 'How the warehouse is organized and how long it takes to get right.',
        links: [
          { slug: 'bronze-silver-gold-ecommerce-order-example', note: 'Raw, cleaned, and reporting layers, shown with a single order.' },
          { slug: 'why-reliable-ecommerce-reporting-takes-time', note: 'Why connecting a source is the start, not the finish.' },
          { slug: 'power-bi-vs-tableau-ecommerce', note: 'Choosing a BI tool for ecommerce dashboards.' },
        ],
      },
    ],
    compareGroup: 'ecomm',
    tools: [
      { href: '/tools/ecommerce-data-stack-quiz', label: 'Build your ecommerce data stack', note: 'One pick per layer, from ingestion to BI, with the reasons and the implementation phase to start with.' },
      { href: '/white-papers/ecommerce-data-stack', label: 'The Ecommerce and Consumer Brand Data Stack (free PDF)', note: 'White paper: tool selection by stage and a five-phase implementation plan.' },
      { href: '/tools/ecommerce-analytics-tool-quiz', label: 'Ecommerce analytics setup quiz', note: 'Eight questions to see whether Shopify reports, a packaged tool, or a warehouse build fits.' },
      { href: '/tools/retention-value-calculator', label: 'Retention value calculator', note: 'What a better 90-day repeat rate is worth in revenue and gross profit.' },
      { href: '/tools/reporting-time-calculator', label: 'Reporting time calculator', note: 'Put a number on the hours spent building and reconciling reports by hand.' },
      { href: '/downloads/ecommerce-metric-dictionary/ecommerce-metric-dictionary-template.csv', label: 'Ecommerce metric dictionary template (CSV)', note: 'Fifteen sample metric entries with grain, date, sources, and owner to fill in with your team.' },
    ],
    services: [{ href: '/ecommerce', label: 'Ecommerce data warehouses and reporting' }],
    faqs: [
      {
        question: 'Why does Shopify revenue not match what finance reports?',
        answer: 'Usually because of timing and definitions: refunds, discounts, taxes, shipping, and which date an order belongs to. The revenue and refund guides above walk through each difference.',
      },
      {
        question: 'Can a packaged ecommerce analytics app replace a warehouse?',
        answer: 'For many smaller brands, yes. A warehouse earns its cost when you need sources the app does not support, your own margin and fee logic, or one model shared by several tools. The comparisons below cover specific apps.',
      },
      {
        question: 'What should be connected first?',
        answer: 'The storefront and marketplace orders, then fees and costs, then marketing spend. Revenue that matches the books comes before attribution.',
      },
    ],
    updated: '2026-11-04',
  },
];

export function getTopic(slug: string): Topic | undefined {
  return topics.find((topic) => topic.slug === slug);
}

/** The hub a post belongs to, chosen by its first matching tag. */
export function getTopicForTags(tags: string[]): Topic | undefined {
  for (const tag of tags) {
    const topic = topics.find((candidate) => candidate.tag === tag);
    if (topic) return topic;
  }
  return undefined;
}
